import { useState, useEffect, createContext, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../lib/auth';
import { useInvestigationTour } from './InvestigationHelp';
import {
    Sparkles,
    Building2,
    MapPin,
    Target,
    Users,
    CreditCard,
    CheckCircle,
    X,
    ChevronRight,
    ChevronLeft,
    PartyPopper,
    HelpCircle,
    RotateCcw
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from './ui/dialog';

// Tour Context for global state management
const TourContext = createContext(null);

export const useTour = () => {
    const context = useContext(TourContext);
    if (!context) {
        throw new Error('useTour must be used within a TourProvider');
    }
    return context;
};

// Tour Provider Component
export const TourProvider = ({ children }) => {
    const { user, isSuperAdmin, isClientAdmin } = useAuth();
    const [showTour, setShowTour] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [tourCompleted, setTourCompleted] = useState(() => {
        // Initialize from localStorage on mount
        if (typeof window !== 'undefined' && user?.id) {
            return !!localStorage.getItem(`yulontap_tour_completed_${user.id}`);
        }
        return false;
    });

    // Check if tour should be shown - ONLY for new users after password change
    useEffect(() => {
        if (!user || (!isSuperAdmin() && !isClientAdmin())) return;
        
        const tourKey = `yulontap_tour_completed_${user.id}`;
        const skippedKey = `yulontap_tour_skipped_${user.id}`;
        const newUserTourKey = `yulontap_new_user_tour_shown_${user.id}`;
        const completed = localStorage.getItem(tourKey);
        const skipped = localStorage.getItem(skippedKey);
        const newUserTourShown = localStorage.getItem(newUserTourKey);
        
        // Check if this is a new user who just changed their password
        // We detect this by checking if the user was redirected from password change
        const justChangedPassword = sessionStorage.getItem('yulontap_just_changed_password');
        
        // Only show tour automatically if:
        // 1. User just changed their password (new user flow)
        // 2. Tour not completed and not skipped
        // 3. New user tour hasn't been shown before
        if (justChangedPassword && !completed && !skipped && !newUserTourShown) {
            sessionStorage.removeItem('yulontap_just_changed_password');
            localStorage.setItem(newUserTourKey, 'true');
            const timer = setTimeout(() => {
                setShowTour(true);
            }, 1500);
            return () => clearTimeout(timer);
        }
    }, [user, isSuperAdmin, isClientAdmin]);

    const startTour = () => {
        setCurrentStep(0);
        setShowTour(true);
    };

    const skipTour = () => {
        if (user) {
            localStorage.setItem(`yulontap_tour_skipped_${user.id}`, 'true');
        }
        setShowTour(false);
        setCurrentStep(0);
    };

    const completeTour = () => {
        if (user) {
            localStorage.setItem(`yulontap_tour_completed_${user.id}`, 'true');
        }
        setTourCompleted(true);
        setShowTour(false);
        setCurrentStep(0);
    };

    const nextStep = () => {
        setCurrentStep(prev => prev + 1);
    };

    const prevStep = () => {
        setCurrentStep(prev => Math.max(0, prev - 1));
    };

    const goToStep = (step) => {
        setCurrentStep(step);
    };

    const resetTour = () => {
        if (user) {
            localStorage.removeItem(`yulontap_tour_completed_${user.id}`);
            localStorage.removeItem(`yulontap_tour_skipped_${user.id}`);
        }
        setTourCompleted(false);
        setCurrentStep(0);
        setShowTour(true);
    };

    return (
        <TourContext.Provider value={{
            showTour,
            setShowTour,
            currentStep,
            setCurrentStep,
            tourCompleted,
            startTour,
            skipTour,
            completeTour,
            nextStep,
            prevStep,
            goToStep,
            resetTour
        }}>
            {children}
            <QuickTourModal />
        </TourContext.Provider>
    );
};

// Tour Steps Configuration
const TOUR_STEPS = [
    {
        id: 'welcome',
        title: 'Welcome to YulOnTap!',
        description: "Let's get you set up in 5 minutes. This quick tour will guide you through the essential steps to start generating reviews for your clients.",
        icon: Sparkles,
        iconColor: 'bg-gradient-to-br from-blue-500 to-purple-600',
        action: null,
        tip: "You can restart this tour anytime from the Help menu"
    },
    {
        id: 'organization',
        title: 'Step 1: Add an Organization',
        description: "Organizations are your client accounts (e.g., restaurant chains). Each organization can have multiple locations and employees using Tap Engine Cards.",
        icon: Building2,
        iconColor: 'bg-purple-500',
        action: {
            label: 'Add Organization',
            path: '/admin/clients',
            highlight: 'add-client-btn'
        },
        tip: "Example: 'Joe's Pizza' or 'Foodtastic Restaurant Group'"
    },
    {
        id: 'location',
        title: 'Step 2: Add a Location',
        description: "Locations are physical places where your clients operate (e.g., individual restaurant branches). Each location tracks its own performance metrics.",
        icon: MapPin,
        iconColor: 'bg-emerald-500',
        action: {
            label: 'Add Location',
            path: '/locations',
            highlight: 'add-location-btn'
        },
        tip: "Example: 'Downtown Branch' or '123 Main Street'"
    },
    {
        id: 'baselines',
        title: 'Step 3: Set Baseline Values',
        description: "Baseline values are your client's starting metrics (reviews, ratings) BEFORE using YulOnTap. This lets you track exactly how many NEW reviews your cards generate.",
        icon: Target,
        iconColor: 'bg-amber-500',
        action: {
            label: 'Set Baselines',
            path: '/locations',
            highlight: 'quick-set-baselines-btn'
        },
        tip: "Check your client's current Google review count and rating"
    },
    {
        id: 'employees',
        title: 'Step 4: Add Employees',
        description: "Employees are team members who will use Tap Engine Cards. Track each employee's performance by seeing how many reviews they generate.",
        icon: Users,
        iconColor: 'bg-blue-500',
        action: {
            label: 'Add Employee',
            path: '/employees',
            highlight: 'add-employee-btn'
        },
        tip: "Start with managers or top performers"
    },
    {
        id: 'cards',
        title: 'Step 5: Assign Cards',
        description: "Finally, add NFC cards and assign them to employees. When customers tap a card, they'll be directed to leave a review on your chosen platform.",
        icon: CreditCard,
        iconColor: 'bg-indigo-500',
        action: {
            label: 'Add Cards',
            path: '/cards',
            highlight: 'add-card-btn'
        },
        tip: "You can upload multiple card IDs via CSV"
    },
    {
        id: 'complete',
        title: "You're All Set!",
        description: "Congratulations! Your client is ready to start generating reviews. Watch the Dashboard to see taps and reviews come in. You can explore the ROI Dashboard, Executive Summary, and other analytics features.",
        icon: PartyPopper,
        iconColor: 'bg-gradient-to-br from-emerald-500 to-teal-600',
        action: {
            label: 'Go to Dashboard',
            path: '/dashboard'
        },
        tip: "Pro tip: Set up automated employee notifications to boost engagement"
    }
];

// Quick Tour Modal Component
const QuickTourModal = () => {
    const navigate = useNavigate();
    const { 
        showTour, 
        currentStep, 
        skipTour, 
        completeTour, 
        nextStep, 
        prevStep,
        goToStep 
    } = useTour();

    const step = TOUR_STEPS[currentStep];
    const isLastStep = currentStep === TOUR_STEPS.length - 1;
    const isFirstStep = currentStep === 0;

    const handleAction = () => {
        if (step.action?.path) {
            navigate(step.action.path);
        }
        
        if (isLastStep) {
            completeTour();
        } else {
            nextStep();
        }
    };

    const StepIcon = step?.icon || Sparkles;

    if (!showTour) return null;

    return (
        <Dialog open={showTour} onOpenChange={(open) => !open && skipTour()}>
            <DialogContent className="sm:max-w-lg p-0 overflow-hidden">
                {/* Header with gradient */}
                <div className={`${step.iconColor} p-6 text-white`}>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-xl bg-background/20 flex items-center justify-center">
                                <StepIcon className="w-6 h-6" />
                            </div>
                            <div>
                                <Badge className="bg-background/20 text-white mb-1">
                                    {isFirstStep ? 'Quick Tour' : `Step ${currentStep} of ${TOUR_STEPS.length - 2}`}
                                </Badge>
                                <DialogTitle className="text-white text-xl">
                                    {step.title}
                                </DialogTitle>
                            </div>
                        </div>
                        <button 
                            onClick={skipTour}
                            className="text-white/70 hover:text-white transition-colors"
                            aria-label="Close tour"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6">
                    <DialogDescription className="text-muted-foreground text-base mb-4">
                        {step.description}
                    </DialogDescription>

                    {/* Tip */}
                    {step.tip && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6">
                            <p className="text-sm text-blue-700 flex items-start gap-2">
                                <HelpCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                <span><strong>Tip:</strong> {step.tip}</span>
                            </p>
                        </div>
                    )}

                    {/* Progress Dots */}
                    <div className="flex justify-center gap-2 mb-6">
                        {TOUR_STEPS.map((_, idx) => (
                            <button
                                key={idx}
                                onClick={() => goToStep(idx)}
                                className={`w-2.5 h-2.5 rounded-full transition-all ${
                                    idx === currentStep 
                                        ? 'bg-blue-600 w-6' 
                                        : idx < currentStep 
                                            ? 'bg-emerald-500' 
                                            : 'bg-slate-200 dark:bg-slate-600'
                                }`}
                                aria-label={`Go to step ${idx + 1}`}
                            />
                        ))}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-between">
                        <div>
                            {!isFirstStep && (
                                <Button 
                                    variant="ghost" 
                                    onClick={prevStep}
                                    className="text-muted-foreground"
                                >
                                    <ChevronLeft className="w-4 h-4 mr-1" />
                                    Back
                                </Button>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            {!isLastStep && (
                                <Button 
                                    variant="ghost" 
                                    onClick={skipTour}
                                    className="text-muted-foreground"
                                >
                                    Skip Tour
                                </Button>
                            )}
                            <Button 
                                onClick={handleAction}
                                className={isLastStep ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-blue-600 hover:bg-blue-700'}
                            >
                                {step.action?.label || (isLastStep ? 'Finish' : 'Next')}
                                {!isLastStep && <ChevronRight className="w-4 h-4 ml-1" />}
                                {isLastStep && <CheckCircle className="w-4 h-4 ml-1" />}
                            </Button>
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

// Help Menu Component (to be used in Sidebar)
export const HelpMenu = () => {
    const { t } = useTranslation();
    const { resetTour, tourCompleted } = useTour();
    const { openHelpCenter, start: startInvTour } = useInvestigationTour();
    const [isOpen, setIsOpen] = useState(false);

    return (
        <div className="relative">
            <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsOpen(!isOpen)}
                className="w-full justify-start text-muted-foreground dark:text-slate-300 hover:bg-slate-100 dark:bg-slate-700 dark:hover:bg-slate-700"
                data-testid="sidebar-help-button"
            >
                <HelpCircle className="w-4 h-4 mr-2" />
                {t('nav.help')}
            </Button>

            {isOpen && (
                <>
                    <div
                        className="fixed inset-0 z-40"
                        onClick={() => setIsOpen(false)}
                    />
                    <div className="absolute bottom-full left-0 mb-2 w-64 bg-background dark:bg-slate-800 rounded-lg shadow-lg border dark:border-slate-700 z-50 py-2" data-testid="sidebar-help-menu">
                        <button
                            onClick={() => { setIsOpen(false); openHelpCenter(); }}
                            className="w-full px-4 py-2 text-left text-sm hover:bg-slate-100 dark:bg-slate-700 dark:hover:bg-slate-700 flex items-center gap-2 text-foreground dark:text-slate-200"
                            data-testid="help-menu-open-help-center"
                        >
                            <HelpCircle className="w-4 h-4" />
                            {t('nav.helpCenter')}
                        </button>
                        <button
                            onClick={() => { setIsOpen(false); startInvTour(); }}
                            className="w-full px-4 py-2 text-left text-sm hover:bg-slate-100 dark:bg-slate-700 dark:hover:bg-slate-700 flex items-center gap-2 text-foreground dark:text-slate-200"
                            data-testid="help-menu-open-inv-tour"
                        >
                            <RotateCcw className="w-4 h-4" />
                            {t('nav.openWalkthrough')}
                        </button>
                        <button
                            onClick={() => {
                                resetTour();
                                setIsOpen(false);
                            }}
                            className="w-full px-4 py-2 text-left text-sm hover:bg-slate-100 dark:bg-slate-700 dark:hover:bg-slate-700 flex items-center gap-2 text-foreground dark:text-slate-200"
                            data-testid="help-menu-start-tour"
                        >
                            <RotateCcw className="w-4 h-4" />
                            {tourCompleted ? t('nav.restartTour') : t('nav.startQuickTour')}
                        </button>
                        <a
                            href="mailto:support@yulontap.com"
                            className="w-full px-4 py-2 text-left text-sm hover:bg-slate-100 dark:bg-slate-700 dark:hover:bg-slate-700 flex items-center gap-2 text-foreground dark:text-slate-200"
                            data-testid="help-menu-contact-support"
                        >
                            <HelpCircle className="w-4 h-4" />
                            {t('nav.contactSupport')}
                        </a>
                    </div>
                </>
            )}
        </div>
    );
};

// Onboarding Progress Card (for Dashboard)
export const OnboardingProgress = ({ organizations = [], locations = [], employees = [], cards = [] }) => {
    const { t } = useTranslation();
    const { startTour, tourCompleted } = useTour();
    const [dismissed, setDismissed] = useState(false);

    // Check what's been completed
    const steps = [
        { id: 'org', label: 'Add an organization', complete: organizations.length > 0 },
        { id: 'loc', label: 'Add a location', complete: locations.length > 0 },
        { id: 'baseline', label: 'Set baseline values', complete: locations.some(l => l.baseline_values?.baseline_date) },
        { id: 'emp', label: 'Add an employee', complete: employees.length > 0 },
        { id: 'card', label: 'Assign a card', complete: cards.some(c => c.assigned_to) }
    ];

    const completedCount = steps.filter(s => s.complete).length;
    const allComplete = completedCount === steps.length;
    const progress = (completedCount / steps.length) * 100;

    // Don't show if all complete or dismissed
    if (allComplete || dismissed || tourCompleted) return null;

    return (
        <div className="bg-gradient-to-br from-emerald-50 to-blue-50 border border-emerald-200 rounded-xl p-5">
            <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center">
                        <Sparkles className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <h3 className="font-semibold text-foreground">{t('quickTour.gettingStarted')}</h3>
                        <p className="text-sm text-muted-foreground">{completedCount} of {steps.length} complete</p>
                    </div>
                </div>
                <button 
                    onClick={() => setDismissed(true)}
                    className="text-muted-foreground hover:text-muted-foreground"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>

            {/* Progress bar */}
            <div className="h-2 bg-slate-200 dark:bg-slate-600 rounded-full mb-4 overflow-hidden">
                <div 
                    className="h-full bg-emerald-500 rounded-full transition-all duration-500"
                    style={{ width: `${progress}%` }}
                />
            </div>

            {/* Steps */}
            <div className="space-y-2 mb-4">
                {steps.map((step, idx) => (
                    <div 
                        key={step.id}
                        className={`flex items-center gap-3 p-2 rounded-lg ${step.complete ? 'bg-background/50' : ''}`}
                    >
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                            step.complete ? 'bg-emerald-500' : 'border-2 border-slate-300'
                        }`}>
                            {step.complete && <CheckCircle className="w-3 h-3 text-white" />}
                        </div>
                        <span className={`flex-1 text-sm ${step.complete ? 'text-muted-foreground line-through' : 'text-foreground'}`}>
                            {step.label}
                        </span>
                    </div>
                ))}
            </div>

            <Button 
                variant="outline" 
                size="sm" 
                onClick={startTour}
                className="w-full"
            >
                <HelpCircle className="w-4 h-4 mr-2" />
                Need Help? Start Tour
            </Button>
        </div>
    );
};

export default QuickTourModal;
